/**
 * Update Button Component
 *
 * Shows an update button in the header when a new version is available.
 * - Force update: No dismiss option, must update
 * - Optional update: Can dismiss (hides for current session only)
 * Performs a hard reload to update the web application.
 *
 * This component subscribes to version info from versionControlService,
 * which receives version data from the notification API (no separate API call needed).
 */

import React, { useEffect, useState } from 'react';
import { RefreshCw, Download, AlertTriangle, X } from 'lucide-react';
import versionControlService, { VersionCheckResponse } from '../../services/versionControl.service';

// Session storage key for dismissed updates
const DISMISSED_VERSION_KEY = 'update_dismissed_version';

const UpdateButton: React.FC = () => {
  // Initialize from cached version info (for immediate render if available)
  const [versionInfo, setVersionInfo] = useState<VersionCheckResponse | null>(
    () => versionControlService.getCachedVersionInfo()
  );
  const [showTooltip, setShowTooltip] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  /**
   * Handle update button click
   */
  const handleUpdate = () => {
    setIsUpdating(true);
    // Small delay to show the loading state
    setTimeout(() => {
      versionControlService.performUpdate();
    }, 500);
  };

  /**
   * Handle dismiss for optional updates
   * Stores in sessionStorage so it shows again on next session/refresh
   */
  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (versionInfo?.latestVersion) {
      sessionStorage.setItem(DISMISSED_VERSION_KEY, versionInfo.latestVersion);
      setIsDismissed(true);
    }
  };

  // Subscribe to version info updates from notification API
  useEffect(() => {
    const unsubscribe = versionControlService.subscribe((info) => {
      setVersionInfo(info);
      // Check if this version was dismissed in current session
      if (info?.latestVersion) {
        const dismissedVersion = sessionStorage.getItem(DISMISSED_VERSION_KEY);
        setIsDismissed(dismissedVersion === info.latestVersion);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Don't render if no update available
  if (!versionInfo?.updateAvailable) {
    return null;
  }

  // Determine button style based on update type
  const isForceUpdate = versionInfo.forceUpdate;

  // Don't render if dismissed (only for optional updates, force updates can't be dismissed)
  if (isDismissed && !isForceUpdate) {
    return null;
  }

  const buttonClasses = isForceUpdate
    ? 'bg-red-500 hover:bg-red-600 text-white'
    : 'bg-custom-500 hover:bg-custom-600 text-white';

  return (
    <div className="relative flex items-center h-header">
      {/* Update Button */}
      <button
        onClick={handleUpdate}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        disabled={isUpdating}
        className={`
          inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium
          transition-all duration-300 shadow-sm hover:shadow-md
          ${isForceUpdate ? 'rounded-lg' : 'rounded-l-lg'}
          ${buttonClasses}
          ${isUpdating ? 'opacity-75 cursor-wait' : ''}
        `}
      >
        {isUpdating ? (
          <>
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="hidden sm:inline">Updating...</span>
          </>
        ) : isForceUpdate ? (
          <>
            <AlertTriangle className="w-4 h-4" />
            <span className="hidden sm:inline">Update Required</span>
            <span className="sm:hidden">Update</span>
          </>
        ) : (
          <>
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Update Available</span>
            <span className="sm:hidden">Update</span>
          </>
        )}
      </button>

      {/* Dismiss Button - Only for optional updates */}
      {!isForceUpdate && !isUpdating && (
        <button
          onClick={handleDismiss}
          className="inline-flex items-center justify-center px-2 py-1.5 rounded-r-lg text-sm font-medium
            transition-all duration-300 bg-custom-600 hover:bg-custom-700 text-white"
          title="Dismiss for now"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {/* Tooltip */}
      {showTooltip && !isUpdating && (
        <div className="absolute top-full right-0 mt-2 w-72 bg-white dark:bg-zink-700 border border-slate-200 dark:border-zink-600 rounded-lg shadow-lg p-4 z-50">
          <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 p-2 rounded-full ${isForceUpdate ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
              {isForceUpdate ? (
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              ) : (
                <Download className="w-5 h-5 text-green-600 dark:text-green-400" />
              )}
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900 dark:text-zink-50">
                {versionInfo.updateTitle || (isForceUpdate ? 'Critical Update Required' : 'New Version Available')}
              </h4>
              <p className="text-sm text-slate-500 dark:text-zink-400 mt-1">
                {versionInfo.updateMessage || `Version ${versionInfo.latestVersion} is now available. You are currently on version ${versionInfo.clientVersion}.`}
              </p>
              {versionInfo.releaseNotes && (
                <p className="text-xs text-slate-400 dark:text-zink-500 mt-2">
                  {versionInfo.releaseNotes}
                </p>
              )}
              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-zink-600">
                <span className="text-xs text-slate-400 dark:text-zink-500">
                  v{versionInfo.clientVersion} → v{versionInfo.latestVersion}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateButton;
